import { DataSource } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { RolloutPlanListItems } from '@ottobock/remote-update/domain';
import { merge, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { compareFirmwareVersions } from '../../sortFirmwareVersions';

/**
 * Data source for the RolloutPlanTable view. This class should
 * encapsulate all logic for fetching and manipulating the displayed data
 * (including sorting, pagination, and filtering).
 */
export class RolloutPlanTableDataSource extends DataSource<RolloutPlanListItems> {
  data: RolloutPlanListItems[] = [];
  paginator: MatPaginator | undefined;
  sort: MatSort | undefined;

  constructor(items: RolloutPlanListItems[]) {
    super();
    this.data.push(...items);
  }

  setRolloutPlanData(data: RolloutPlanListItems[]) {
    this.data = [...data];
  }


connect(): Observable<RolloutPlanListItems[]> {
  if (!this.paginator || !this.sort) {
    throw Error('Paginator and sort must be set before connecting.');
  }

  return merge(
    this.sort.sortChange,
    this.paginator.page
  ).pipe(
    startWith(null), // initial emission
    map(() => {
      const sortedData = this.getSortedData([...this.data]);
      return this.getPagedData(sortedData);
    })
  );
}


  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  disconnect(): void {}

  /**
   * Paginate the data (client-side). If you're using server-side pagination,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getPagedData(data: RolloutPlanListItems[]): RolloutPlanListItems[] {
    if (this.paginator) {
      const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
      // return data.splice(startIndex, this.paginator.pageSize);
      return data.slice(startIndex, startIndex + this.paginator.pageSize);

    } else {
      return data;
    }
  }

  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: RolloutPlanListItems[]): RolloutPlanListItems[] {
    if (!this.sort || !this.sort.active || this.sort.direction === '') {
      return data;
    }

    const isAsc = this.sort.direction === 'asc';
    return data.sort((a, b) => {
      switch (this.sort?.active) {
        case 'packageVersion':
          return compareFirmwareVersions(a.packageVersion, b.packageVersion, isAsc);
        case 'state':
          return compareRolloutPlanStates(a.status, b.status, isAsc);
        case 'updatedAt':
          return compare(new Date(a.updatedAt).getTime(), new Date(b.updatedAt).getTime(), isAsc);
        default:
          return 0;
      }
    });
  }
}

/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(
  a: string | number,
  b: string | number,
  isAsc: boolean
): number {
  if (a === b) return 0;
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}

const stateOrder: Record<string, number> = {
  Draft: 1,
  'Ready for review': 2,
  Active: 3,
  Inactive: 4,
  Stopped: 5
};

function compareRolloutPlanStates(a: string, b: string, isAsc: boolean): number {
  const orderA = stateOrder[a]; 
  const orderB = stateOrder[b];

  const diff = orderA - orderB;
  return isAsc ? diff : -diff;
}


